import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { Router } from '@angular/router';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { AngularFireStorage, AngularFireUploadTask } from '@angular/fire/compat/storage';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { BoardThread } from '../firestore-models/board-thread';
import { ApiBoardThread, ApiBoardFile, ApiSetBoardThreadRequest } from '../api-client-module';

import { SessionService } from '../session.service';
import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';
import { TranslateService } from '@ngx-translate/core';

import { BoardThreadDialogComponent } from './board-thread-dialog/board-thread-dialog.component';


@Component({
  selector: 'app-board-thread',
  templateUrl: './board-thread.component.html',
  styleUrls: ['./board-thread.component.css'],
  standalone: false,
})
export class BoardThreadComponent implements OnInit, OnDestroy {

  private boardThreadCollection!: AngularFirestoreCollection<BoardThread>;
  boardThreadObservable!: Observable<BoardThread[]>;
  boardThreadSubscribe!: Subscription;

  //フロア情報テーブル
  displayedColumns: string[] = ['thread_owner', 'created', 'thread_name', 'thread_desc', 'thread_image', 'gotoComment'];
  dataSource = new MatTableDataSource<BoardThread>();
  selection = new SelectionModel<BoardThread>(true, []);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  task!: AngularFireUploadTask;
  owner_email: string = '';


  constructor(
    private router: Router,
    private sessionService: SessionService,
    private configurationService: ConfigurationService,
    private storage: AngularFireStorage,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.boardThreadCollection = this.angularFirestore.collection<BoardThread>(AppConst.FIRESTORE_BOARD_THREAD, ref => ref.orderBy('created', 'desc'));
    this.boardThreadObservable = this.boardThreadCollection.valueChanges({ idField: 'id' });
    this.boardThreadSubscribe = this.boardThreadObservable.subscribe(boardThreads => {
      this.dataSource.data = boardThreads;
    });
    this.owner_email = this.sessionService.auth.currentUser?.email!;
  }

  ngOnDestroy(): void {
    this.boardThreadSubscribe.unsubscribe();
  }

  gotoComment(boardThread: BoardThread): void {
    this.configurationService.setThreadId(boardThread.id!);
    this.router.navigate([AppConst.ROUTING_LINK_BOARD_COMMENT]);
  }

  openBrowseDialog(boardThread: BoardThread): void {
    let apiBoardThread: ApiBoardThread = {
      id: boardThread.id,
      thread_name: boardThread.thread_name,
      thread_desc: boardThread.thread_desc,
      thread_image: boardThread.thread_image,
      thread_owner: boardThread.thread_owner
    };
    const dialogRef = this.dialog.open(BoardThreadDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { boardThread: apiBoardThread, isBrowse: true }
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }

  openEditDialog(boardThread: BoardThread): void {
    let apiBoardThread: ApiBoardThread = {
      id: boardThread.id,
      thread_name: boardThread.thread_name,
      thread_desc: boardThread.thread_desc,
      thread_image: boardThread.thread_image,
      thread_owner: boardThread.thread_owner
    };
    const dialogRef = this.dialog.open(BoardThreadDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { boardThread: apiBoardThread, isBrowse: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.spinnerService.openSpinner();
        if (result.isDelete === false) {
          //  更新処理
          if (!!result.file) {
            //  添付ファイルあり（イメージ再設定の場合）
            const fileName = `${Date.now()}_${result.file.name}`;
            const path = `${AppConst.FILE_UPLOAD_BOARD_DIR_NAME}/${fileName}`;
            const ref = this.storage.ref(path);

            this.task = this.storage.upload(path, result.file);
            this.task.snapshotChanges().pipe(
              finalize(() => {
                ref.getDownloadURL().subscribe(downloadURL => {
                  let boardFile: ApiBoardFile = {
                    file_name: fileName,
                    file_path: path,
                    download_url: downloadURL
                  };
                  let boardThread: ApiBoardThread = {
                    id: result.id,
                    thread_name: result.thread_name,
                    thread_desc: result.thread_desc,
                    thread_image: boardFile,
                    thread_owner: this.sessionService.auth.currentUser?.email!,
                  };
                  let request: ApiSetBoardThreadRequest = {
                    access_token: '',
                    change_type: AppConst.DATA_CHANGE_TYPE_UPDATE,
                    board_threads: [boardThread]
                  }
                  this.apiClientService.setBoardThread(request).then(
                    response => {
                      if (response!.ret === AppConst.API_RET_SUCCESS) {
                        this.toastr.success(this.translate.instant('更新しました。'));
                      } else {
                        this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
                      }
                    }
                  ).catch(
                    error => {
                      console.log(error);
                      this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
                    }
                  ).finally(
                    () => {
                      this.spinnerService.closeSpinner();
                    }
                  );
                });
              })
            ).subscribe();
          } else {
            //  添付ファイルなし
            let boardThread: ApiBoardThread;
            if (!!result.thread_image) {
              //  既存イメージあり
              let boardFile: ApiBoardFile = {
                file_name: result.thread_image.file_name,
                file_path: result.thread_image.file_path,
                download_url: result.thread_image.download_url
              };
              boardThread = {
                id: result.id,
                thread_name: result.thread_name,
                thread_desc: result.thread_desc,
                thread_image: boardFile,
                thread_owner: this.sessionService.auth.currentUser?.email!,
              };
            } else {
              //  既存イメージなし
              boardThread = {
                id: result.id,
                thread_name: result.thread_name,
                thread_desc: result.thread_desc,
                thread_owner: this.sessionService.auth.currentUser?.email!,
              };
            }
            let request: ApiSetBoardThreadRequest = {
              access_token: '',
              change_type: AppConst.DATA_CHANGE_TYPE_UPDATE,
              board_threads: [boardThread]
            }
            this.apiClientService.setBoardThread(request).then(
              response => {
                if (response!.ret === AppConst.API_RET_SUCCESS) {
                  this.toastr.success(this.translate.instant('更新しました。'));
                } else {
                  this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
                }
              }
            ).catch(
              error => {
                console.log(error);
                this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
              }
            ).finally(
              () => {
                this.spinnerService.closeSpinner();
              }
            );
          }
        } else {
          //  削除処理
          let request: ApiSetBoardThreadRequest = {
            access_token: '',
            change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
            board_threads: [boardThread]
          }
          this.apiClientService.setBoardThread(request).then(
            response => {
              if (response!.ret === AppConst.API_RET_SUCCESS) {
                this.toastr.success(this.translate.instant('削除しました。'));
              } else {
                this.toastr.error(this.translate.instant('削除できませんでした。'));
              }
            }
          ).catch(
            error => {
              console.log(error);
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          ).finally(
            () => {
              this.spinnerService.closeSpinner();
            }
          );
        }
      }
    });
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(BoardThreadDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { boardThread: null, isBrowse: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.spinnerService.openSpinner();
        if (!!result.file) {
          //  添付ファイルあり
          const fileName = `${Date.now()}_${result.file.name}`;
          const path = `${AppConst.FILE_UPLOAD_BOARD_DIR_NAME}/${fileName}`;
          const ref = this.storage.ref(path);

          this.task = this.storage.upload(path, result.file);
          this.task.snapshotChanges().pipe(
            finalize(() => {
              ref.getDownloadURL().subscribe(downloadURL => {
                let boardFile: ApiBoardFile = {
                  file_name: fileName,
                  file_path: path,
                  download_url: downloadURL
                };
                let boardThread: ApiBoardThread = {
                  thread_name: result.thread_name,
                  thread_desc: result.thread_desc,
                  thread_image: boardFile,
                  thread_owner: this.sessionService.auth.currentUser?.email!,
                };
                let request: ApiSetBoardThreadRequest = {
                  access_token: '',
                  change_type: AppConst.DATA_CHANGE_TYPE_ADD,
                  board_threads: [boardThread]
                }
                this.apiClientService.setBoardThread(request).then(
                  response => {
                    if (response!.ret === AppConst.API_RET_SUCCESS) {
                      this.toastr.success(this.translate.instant('登録しました。'));
                    } else {
                      this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
                    }
                  }
                ).catch(
                  error => {
                    console.log(error);
                    this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
                  }
                ).finally(
                  () => {
                    this.spinnerService.closeSpinner();
                  }
                );
              });
            })
          ).subscribe();
        } else {
          //  添付ファイルなし
          let boardThread: ApiBoardThread = {
            thread_name: result.thread_name,
            thread_desc: result.thread_desc,
            thread_owner: this.sessionService.auth.currentUser?.email!,
          };
          let request: ApiSetBoardThreadRequest = {
            access_token: '',
            change_type: AppConst.DATA_CHANGE_TYPE_ADD,
            board_threads: [boardThread]
          }
          this.apiClientService.setBoardThread(request).then(
            response => {
              if (response!.ret === AppConst.API_RET_SUCCESS) {
                this.toastr.success(this.translate.instant('登録しました。'));
              } else {
                this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
              }
            }
          ).catch(
            error => {
              console.log(error);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          ).finally(
            () => {
              this.spinnerService.closeSpinner();
            }
          );
        }
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'created':
          return compare(a.created.toString(), b.created.toString(), isAsc);
        case 'thread_name':
          return compare(a.thread_name, b.thread_name, isAsc);
        case 'thread_desc':
          return compare(a.thread_desc, b.thread_desc, isAsc);
        case 'thread_owner':
          return compare(a.thread_owner, b.thread_owner, isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
